import type {
  Attribute,
  AttributeDataType,
  AttributeValue,
  CreateAttributeRequest,
  CreateAttributeValueRequest,
  SwatchInput,
  UpdateAttributeRequest,
  UpdateAttributeValueRequest,
} from '@nucleus-commerce/api-client/admin';

import type {
  AttributeFormInput,
  AttributeFormValues,
  AttributeValueFormInput,
  AttributeValueFormValues,
} from '../schemas/attribute-form-schema';

/** The backend's rules: only these types may define variants, carry a unit, or have values. */
export const canBeOption = (type: AttributeDataType) => type === 'select' || type === 'number';
export const hasUnit = (type: AttributeDataType) => type === 'number' || type === 'dimension';
export const hasValues = (type: AttributeDataType) => type === 'select' || type === 'multiselect';

const fold = (label: string) => label.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '');

/** `^[a-z][a-z0-9_]*$`, so leading digits and symbols are dropped. */
export function attributeKeyFrom(label: string): string {
  return fold(label)
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^[^a-z]+|_+$/g, '')
    .slice(0, 64);
}

/** `^[a-z0-9][a-z0-9_.-]*$`: the stored value carts, variants and filters reference. */
export function valueCodeFrom(label: string): string {
  return fold(label)
    .replace(/[^a-z0-9_.-]+/g, '_')
    .replace(/^[^a-z0-9]+|_+$/g, '')
    .slice(0, 120);
}

export function emptyAttributeForm(): AttributeFormInput {
  return {
    label: '',
    key: '',
    dataType: 'select',
    unit: '',
    usableAsOption: false,
    isFilterable: false,
    isSearchable: false,
    isComparable: false,
    showOnProductPage: true,
  };
}

export function fromAttribute(row: Attribute): AttributeFormInput {
  return {
    label: row.label,
    key: row.key,
    dataType: row.dataType,
    unit: row.unit ?? '',
    usableAsOption: row.usableAsOption,
    isFilterable: row.isFilterable,
    isSearchable: row.isSearchable,
    isComparable: row.isComparable,
    showOnProductPage: row.showOnProductPage,
  };
}

/** Flags every type takes; `usableAsOption` is forced off where the backend would refuse it. */
function flags(values: AttributeFormValues) {
  return {
    usableAsOption: canBeOption(values.dataType) && values.usableAsOption,
    isFilterable: values.isFilterable,
    isSearchable: values.isSearchable,
    isComparable: values.isComparable,
    showOnProductPage: values.showOnProductPage,
  };
}

export function toAttributeCreate(values: AttributeFormValues): CreateAttributeRequest {
  return {
    key: values.key,
    label: values.label,
    dataType: values.dataType,
    ...(hasUnit(values.dataType) && values.unit ? { unit: values.unit } : {}),
    ...flags(values),
  };
}

/** Key and data type never change; unit is sent only for the types that show one. */
export function toAttributeUpdate(values: AttributeFormValues): UpdateAttributeRequest {
  return {
    label: values.label,
    ...(hasUnit(values.dataType) ? { unit: values.unit || null } : {}),
    ...flags(values),
  };
}

export function emptyAttributeValueForm(): AttributeValueFormInput {
  return { label: '', value: '', swatch: '', swatchMediaId: null, skuCode: '' };
}

export function fromAttributeValue(row: AttributeValue): AttributeValueFormInput {
  return {
    label: row.label,
    value: row.value,
    swatch: row.swatch?.color ?? '',
    swatchMediaId: row.swatch?.mediaId ?? null,
    skuCode: row.skuCode ?? '',
  };
}

/** A swatch is a color, an image, or both; neither means no swatch. */
export function toSwatch(color: string, mediaId: string | null): SwatchInput | null {
  if (color === '' && !mediaId) return null;
  return { ...(color ? { color } : {}), ...(mediaId ? { mediaId } : {}) };
}

export function toAttributeValueCreate(
  values: AttributeValueFormValues,
): CreateAttributeValueRequest {
  const swatch = toSwatch(values.swatch, values.swatchMediaId);
  return {
    value: values.value,
    label: values.label,
    ...(swatch ? { swatch } : {}),
    ...(values.skuCode ? { skuCode: values.skuCode } : {}),
  };
}

export function toAttributeValueUpdate(
  values: AttributeValueFormValues,
): UpdateAttributeValueRequest {
  return {
    label: values.label,
    swatch: toSwatch(values.swatch, values.swatchMediaId),
    skuCode: values.skuCode || null,
  };
}

/** Values still without a SKU code, which the derive rule renders from their stored value. */
export const withoutSkuCode = (values: readonly AttributeValue[]) =>
  values.filter((v) => v.skuCode === null);

/** Values in display order, the backend's: position, then the stored value. */
export const byPosition = (a: AttributeValue, b: AttributeValue) =>
  a.position - b.position || a.value.localeCompare(b.value);

/**
 * Every value id with one value moved one place up or down: what the reorder endpoint takes.
 * Empty when the move falls off either end, so the caller sends nothing.
 */
export function movedValueIds(
  values: readonly AttributeValue[],
  id: string,
  offset: -1 | 1,
): string[] {
  const order = [...values].sort(byPosition);
  const from = order.findIndex((v) => v.id === id);
  const to = from + offset;
  const moved = order[from];
  if (from === -1 || to < 0 || to >= order.length || !moved) return [];
  order.splice(from, 1);
  order.splice(to, 0, moved);
  return order.map((v) => v.id);
}
